// @docs:
//   - docs/core/architecture/SHIPS_PAPERS.md
// Ship's Papers entry packs — reading the API's answer. The pack is a PDF blob;
// what went in and what was left out travel in headers (X-Pack-Included,
// X-Pack-Skipped, Content-Disposition), which a browser only shows the portal
// when the API's CORS exposes them. Without them nothing here claims a count.
// Errors come back as a blob too (the request asked for one), so their JSON
// message has to be read out of it.

import { isAxiosError } from 'axios';

/** vortex-api services/papersExtrasService.ts PACK_MAX_DOCS. */
export const PACK_MAX_DOCS = 40;

/** "Fiji entry — Savusavu, May 2026" → "Fiji-entry-Savusavu-May-2026.pdf". */
export function packFileName(title: string): string {
  const slug = title.normalize('NFC').replace(/[^\p{L}\p{N}\s-]/gu, ' ').trim().replace(/[\s-]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'entry-pack'}.pdf`;
}

function dispositionName(cd: string): string | null {
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(cd);
  if (star) { try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through */ } }
  const plain = /filename\s*=\s*"([^"]+)"/.exec(cd) ?? /filename\s*=\s*([^;]+)/.exec(cd);
  return plain ? plain[1].trim() : null;
}

/** `included` is null when the header is not visible — the pack still downloaded. */
export function parsePackHeaders(headers: Record<string, unknown>, title: string): { fileName: string; included: number | null; skipped: string[] } {
  const get = (k: string) => { const v = headers[k] ?? headers[k.toLowerCase()]; return v == null ? null : String(v); };
  const cd = get('content-disposition');
  const inc = get('x-pack-included');
  const skippedRaw = get('x-pack-skipped');
  let skipped: string[] = [];
  if (skippedRaw) { try { skipped = decodeURIComponent(skippedRaw).split('; ').filter(Boolean); } catch { skipped = [skippedRaw]; } }
  const n = inc != null && inc !== '' ? Number(inc) : NaN;
  return { fileName: (cd && dispositionName(cd)) || packFileName(title), included: Number.isFinite(n) ? n : null, skipped };
}

/** Blob.text() where there is one; FileReader elsewhere (older WebViews, jsdom). */
const readText = (b: Blob): Promise<string> => (typeof b.text === 'function' ? b.text() : new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result ?? ''));
  r.onerror = () => reject(r.error);
  r.readAsText(b);
}));

/** The API's `error` message, from a JSON body or a blob holding JSON; `fallback` otherwise. */
export async function blobErrorMessage(e: unknown, fallback: string): Promise<string> {
  if (!isAxiosError(e)) return fallback;
  const data = e.response?.data as unknown;
  if (data && typeof data === 'object' && !(data instanceof Blob)) return (data as { error?: string }).error || fallback;
  if (data instanceof Blob) {
    try {
      const text = await readText(data);
      return (JSON.parse(text) as { error?: string }).error || fallback;
    } catch { return fallback; }
  }
  return fallback;
}
